<template>
  <AppHeader />
  <div class="min-h-screen bg-blue-50">
    <section class="bg-gradient-to-r from-blue-900 to-blue-700 py-5 shadow-sm">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
        <h1 class="text-2xl font-bold text-white">Social Hub</h1>
        <p class="text-sm text-blue-100 mt-1">Discover peers, manage your network, and keep conversations moving.</p>
      </div>
    </section>

    <div v-if="!hasJoinedSocials && !showJoinPrompt" class="max-w-3xl mx-auto px-4 py-8 pb-28">
      <div class="bg-white rounded-lg shadow-sm border border-gray-100 p-8 text-center">
        <span class="material-symbols-outlined text-5xl text-blue-300 mb-4">diversity_3</span>
        <h2 class="text-xl font-semibold text-gray-900 mb-2">Join Social Hub</h2>
        <p class="text-gray-600 mb-6">
          Connect with students from your university, find study partners, and build your academic network.
        </p>
        <button
          type="button"
          @click="showJoinPrompt = true"
          class="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Join Now
        </button>
      </div>
    </div>

    <template v-else-if="hasJoinedSocials">
      <socials-primary-nav
        :active-tab="activeTab"
        :connection-requests-count="connectionRequestsCount"
        @navigate="handleTabNav"
      />

      <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-28 space-y-4">
        <discovery-grid
          v-if="activeTab === 'discover'"
          v-model:search-query="searchQuery"
          :users="filteredUsers"
          :loading="socialUsersLoading"
          :error="socialUsersError"
          :has-more="socialUsersHasMore"
          :active-filter-count="activeFilterCount"
          :filter-chips="activeFilterChips"
          :get-connection-status="getConnectionStatus"
          @connect="connectWithUser"
          @chat="startChatWithUser"
          @view="openProfileDrawer"
          @retry="loadDiscovery(true, true)"
          @load-more="loadDiscovery(false, true)"
          @open-filters="showFilterSheet = true"
          @remove-filter="removeDiscoveryFilter"
        />

        <network-panel
          v-if="activeTab === 'network'"
          :connections="connections"
          :pending-connections="pendingConnections"
          :connection-requests="connectionRequests"
          :get-connection-status="getConnectionStatus"
          @accept-request="acceptRequest"
          @reject-request="rejectRequest"
          @cancel-request="cancelRequest"
          @remove-connection="showRemoveConnectionDialog"
          @go-discover="activeTab = 'discover'"
          @chat="startChatWithUser"
          @view="openProfileDrawer"
        />
      </main>
    </template>

    <join-social-prompt
      v-if="showJoinPrompt"
      @joined="handleJoinedSocials"
      @declined="showJoinPrompt = false"
    />

    <peer-profile-drawer
      v-model="showProfileDrawer"
      :user="selectedPeer"
      :connection-status="selectedPeer ? getConnectionStatus(selectedPeer.id) : null"
      @connect="connectWithUser"
      @chat="startChatWithUser"
    />

    <discovery-filter-sheet
      v-model="showFilterSheet"
      :filters="discoveryFilters"
      :faculties="faculties"
      @apply="applyDiscoveryFilters"
    />

    <app-confirm-dialog
      v-model="showRemoveConfirmation"
      title="Remove Connection"
      message="Are you sure you want to remove this connection? You will no longer be connected."
      type="danger"
      icon="person_remove"
      confirm-text="Remove"
      @confirm="confirmRemoveConnection"
    />
  </div>
  <BottomNav />
</template>

<script>
import { mapState } from 'vuex';
import AppHeader from '@/components/common/AppHeader.vue';
import BottomNav from '@/components/common/BottomNav.vue';
import AppConfirmDialog from '@/components/common/AppConfirmDialog.vue';
import JoinSocialPrompt from '@/components/socials/JoinSocialPrompt.vue';
import DiscoveryGrid from '@/components/socials/DiscoveryGrid.vue';
import NetworkPanel from '@/components/socials/NetworkPanel.vue';
import PeerProfileDrawer from '@/components/socials/PeerProfileDrawer.vue';
import DiscoveryFilterSheet from '@/components/socials/DiscoveryFilterSheet.vue';
import SocialsPrimaryNav from '@/components/socials/SocialsPrimaryNav.vue';
import { useResources } from '@/composables/useResources';

export default {
  name: 'SocialsPage',
  components: {
    AppHeader,
    BottomNav,
    AppConfirmDialog,
    JoinSocialPrompt,
    DiscoveryGrid,
    NetworkPanel,
    PeerProfileDrawer,
    DiscoveryFilterSheet,
    SocialsPrimaryNav
  },
  setup() {
    const { faculties } = useResources();
    return { faculties };
  },
  data() {
    return {
      activeTab: 'discover',
      searchQuery: '',
      discoveryFilters: {
        faculty: '',
        studyYear: '',
        levelOfStudy: ''
      },
      showFilterSheet: false,
      showJoinPrompt: false,
      showProfileDrawer: false,
      selectedPeer: null,
      showRemoveConfirmation: false,
      connectionToRemove: null,
      actionLoading: false,
      searchTimer: null
    };
  },
  computed: {
    ...mapState([
      'user',
      'profile',
      'connections',
      'pendingConnections',
      'connectionRequests',
      'socialUsers',
      'socialUsersLoading',
      'socialUsersHasMore',
      'socialUsersError'
    ]),
    hasJoinedSocials() {
      return this.profile?.joinedSocials === true;
    },
    connectionRequestsCount() {
      return (this.connectionRequests || []).length;
    },
    filteredUsers() {
      return this.$store.getters.filteredSocialUsers(this.searchQuery);
    },
    activeFilterCount() {
      return Object.values(this.discoveryFilters).filter(Boolean).length;
    },
    activeFilterChips() {
      const chips = [];
      const faculty = this.faculties.find(item => item.code === this.discoveryFilters.faculty);

      if (this.discoveryFilters.faculty) {
        chips.push({
          key: 'faculty',
          label: 'Faculty',
          value: faculty?.name || this.discoveryFilters.faculty
        });
      }

      if (this.discoveryFilters.studyYear) {
        chips.push({
          key: 'studyYear',
          label: 'Year',
          value: this.studyYearLabel(this.discoveryFilters.studyYear)
        });
      }

      if (this.discoveryFilters.levelOfStudy) {
        chips.push({
          key: 'levelOfStudy',
          label: 'Level',
          value: this.discoveryFilters.levelOfStudy
        });
      }

      return chips;
    }
  },
  watch: {
    hasJoinedSocials(newValue) {
      if (newValue) {
        this.loadDiscovery(true);
        this.$store.dispatch('fetchUserConnections');
      }
    },
    activeTab(newValue) {
      if (newValue === 'discover') {
        this.loadDiscovery(false);
      }
    },
    searchQuery() {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(() => {
        this.loadDiscovery(true, true);
      }, 350);
    },
    '$route.query.tab': {
      immediate: true,
      handler(tab) {
        if (tab === 'network' || tab === 'connections') {
          this.activeTab = 'network';
        } else {
          this.activeTab = 'discover';
        }
      }
    }
  },
  mounted() {
    if (this.hasJoinedSocials) {
      this.loadDiscovery(false);
      this.$store.dispatch('fetchUserConnections');
    }
  },
  methods: {
    getConnectionStatus(userId) {
      return this.$store.getters.connectionStatusWith(userId);
    },
    async loadDiscovery(reset = false, force = false) {
      if (!this.hasJoinedSocials || this.activeTab !== 'discover') return;
      await this.$store.dispatch('fetchSocialUsersPage', {
        reset,
        force,
        pageSize: 24,
        searchQuery: this.searchQuery,
        filters: this.discoveryFilters
      });
    },
    applyDiscoveryFilters(filters) {
      this.discoveryFilters = { ...filters };
      this.loadDiscovery(true, true);
    },
    removeDiscoveryFilter(filterKey) {
      this.discoveryFilters = {
        ...this.discoveryFilters,
        [filterKey]: ''
      };
      this.loadDiscovery(true, true);
    },
    studyYearLabel(year) {
      const labels = {
        1: '1st Year',
        2: '2nd Year',
        3: '3rd Year',
        4: '4th Year',
        5: '5th Year+'
      };
      return labels[year] || `Year ${year}`;
    },
    async connectWithUser(userId) {
      if (!this.hasJoinedSocials || this.actionLoading) return;

      const status = this.getConnectionStatus(userId);
      if (status === 'connected') {
        this.$store.dispatch('showAlert', {
          message: 'You are already connected with this student.',
          type: 'info'
        });
        return;
      }

      if (status === 'pending') {
        this.$store.dispatch('showAlert', {
          message: 'Connection request already sent.',
          type: 'info'
        });
        return;
      }

      if (status === 'request') {
        await this.acceptRequest(userId);
        return;
      }

      this.actionLoading = true;
      try {
        const success = await this.$store.dispatch('sendConnectionRequest', userId);
        if (!success) throw new Error('Failed to send connection request');
        this.$store.dispatch('showAlert', {
          message: 'Connection request sent.',
          type: 'success'
        });
      } catch (error) {
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to connect with student.',
          type: 'error'
        });
      } finally {
        this.actionLoading = false;
      }
    },
    async acceptRequest(userId) {
      if (this.actionLoading) return;
      this.actionLoading = true;
      try {
        const success = await this.$store.dispatch('acceptConnectionRequest', userId);
        if (!success) throw new Error('Failed to accept connection request');
        this.$store.dispatch('showAlert', {
          message: 'Connection accepted.',
          type: 'success'
        });
      } catch (error) {
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to accept request.',
          type: 'error'
        });
      } finally {
        this.actionLoading = false;
      }
    },
    async rejectRequest(userId) {
      if (this.actionLoading) return;
      this.actionLoading = true;
      try {
        const success = await this.$store.dispatch('rejectConnectionRequest', userId);
        if (!success) throw new Error('Failed to reject connection request');
        this.$store.dispatch('showAlert', {
          message: 'Connection request rejected.',
          type: 'success'
        });
      } catch (error) {
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to reject request.',
          type: 'error'
        });
      } finally {
        this.actionLoading = false;
      }
    },
    async cancelRequest(userId) {
      if (this.actionLoading) return;
      this.actionLoading = true;
      try {
        const success = await this.$store.dispatch('cancelConnectionRequest', userId);
        if (!success) throw new Error('Failed to cancel connection request');
        this.$store.dispatch('showAlert', {
          message: 'Connection request cancelled.',
          type: 'success'
        });
      } catch (error) {
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to cancel request.',
          type: 'error'
        });
      } finally {
        this.actionLoading = false;
      }
    },
    showRemoveConnectionDialog(userId) {
      this.connectionToRemove = userId;
      this.showRemoveConfirmation = true;
    },
    async confirmRemoveConnection() {
      if (!this.connectionToRemove) return;

      try {
        const { removeConnection } = await import('@/composables/useFirestore');
        const result = await removeConnection(this.user.uid, this.connectionToRemove);
        if (!result.success) throw new Error(result.message || 'Failed to remove connection');
        this.$store.commit('removeConnection', this.connectionToRemove);
        this.$store.dispatch('showAlert', {
          message: 'Connection removed.',
          type: 'success'
        });
      } catch (error) {
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to remove connection.',
          type: 'error'
        });
      } finally {
        this.connectionToRemove = null;
      }
    },
    openProfileDrawer(user) {
      this.selectedPeer = user;
      this.showProfileDrawer = true;
    },
    async startChatWithUser(userId) {
      if (!this.hasJoinedSocials) return;
      if (this.getConnectionStatus(userId) !== 'connected') {
        this.$store.dispatch('showAlert', {
          message: 'You can only chat with connections.',
          type: 'info'
        });
        return;
      }

      const userDetails = this.$store.state.socialUserDetailsById[userId] ||
        this.socialUsers.find(user => user.id === userId);
      const currentUser = {
        id: this.user?.uid,
        name: this.profile?.name || this.user?.displayName || '',
        profilePicture: this.profile?.profilePicture || ''
      };
      const otherUser = {
        id: userId,
        name: userDetails?.name || 'Student',
        profilePicture: userDetails?.profilePicture || ''
      };

      try {
        const { createDirectConversation } = await import('@/composables/useChat');
        await createDirectConversation([currentUser.id, otherUser.id], [currentUser, otherUser]);
        this.$router.push(`/chat/${userId}`);
      } catch (error) {
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to start chat.',
          type: 'error'
        });
      }
    },
    async handleJoinedSocials() {
      try {
        await this.$store.dispatch('joinSocials');
        this.showJoinPrompt = false;
        await this.loadDiscovery(true, true);
        this.$store.dispatch('showAlert', {
          message: 'You have joined Social Hub.',
          type: 'success'
        });
      } catch (error) {
        this.$store.dispatch('showAlert', {
          message: error.message || 'Could not join Social Hub.',
          type: 'error'
        });
      }
    },
    handleTabNav(tabKey) {
      if (tabKey === 'chat') {
        this.$router.push('/chat');
      } else if (tabKey === 'posts') {
        this.$router.push('/posts');
      } else if (tabKey === 'hustleup') {
        this.$router.push('/hustleup');
      } else {
        this.activeTab = tabKey;
        this.$router.replace({
          path: '/socials',
          query: tabKey === 'network' ? { tab: 'network' } : {}
        });
      }
    }
  },
  beforeUnmount() {
    window.clearTimeout(this.searchTimer);
  }
};
</script>
